<template>
  <div ref="chart" style="width: 100%; height: 100%;"></div>
</template>

<script>
import * as echarts from "echarts";

export default {
  name: "EChartsBarDouble",
  props: {
    title: { type: String, default: "" },
    labels: Array,
    datasets: Array,
    annualTotals: Array,
    height: { type: String, default: "65vh" },
    selectedMonthName: String
  },
  data: () => ({ 
    chartInstance: null, 
    depthX: 15, 
    depthY: 10, 
    resizeObserver: null 
  }),
  watch: {
    selectedMonthName: "renderChart",
    datasets: { handler: "renderChart", deep: true },
    labels: "renderChart"
  },
  mounted() {
    this.initChart();
    window.addEventListener("resize", this.safeResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.safeResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.chartInstance) this.chartInstance.dispose();
  },
  methods: {

    initChart() {
      if (!this.$refs.chart) return;
      const dom = this.$refs.chart;

      dom.style.width = '100%';
      dom.style.height = '100%';

      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        
        if (entry && entry.contentRect.width > 0 && entry.contentRect.height > 0) {
           
           if (this.chartInstance) {

             this.chartInstance.resize();
             this.renderChart(); 
           } else {

             this.chartInstance = echarts.init(dom);
             this.renderChart();
           }
        }
      });
      
      resizeObserver.observe(dom);
      this.resizeObserver = resizeObserver;

      if (dom.clientWidth > 0 && dom.clientHeight > 0) {
        if (this.chartInstance) this.chartInstance.dispose();
        this.chartInstance = echarts.init(dom);
        this.renderChart();
      }
    },

    safeResize() {
      if (!this.chartInstance || !this.$refs.chart) return;
      if (this.$refs.chart.clientWidth === 0) return;
      this.chartInstance.resize();

      this.renderChart();
    },

    getChartImage() {
      if (!this.chartInstance) return null;

      const dataUrl = this.chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 3, 
        backgroundColor: '#fff',
        excludeComponents: ['toolbox'] 
      });

      return dataUrl;
    },

    renderChart() {
      if (!this.chartInstance || !this.datasets || this.datasets.length < 2) return;

      const containerWidth = this.$refs.chart ? this.$refs.chart.clientWidth : 1000;

      const gridWidth = containerWidth * 0.93;
      const numberOfBars = this.labels.length || 1;
      const spacePerColumn = gridWidth / numberOfBars;

      const dynamicAxisFontSize = Math.min(15, Math.max(10, spacePerColumn / 8));

      const dynamicTitleSize = Math.min(26, Math.max(16, containerWidth * 0.022));
      const dynamicLegendSize = Math.min(16, Math.max(12, containerWidth * 0.014));

      const dynamicBreakWidth = Math.min(120, spacePerColumn - 5);

      const L = {
        pB: `% programado hasta ${this.selectedMonthName} vs anual`,
        eB: `% ejecutado hasta ${this.selectedMonthName} vs anual`,
        pC: `N° de acciones programadas hasta ${this.selectedMonthName}`,
        eC: `N° de acciones ejecutadas hasta ${this.selectedMonthName}`,
        an: `N° acciones anual`
      };

      const option = {
        title: {
          text: this.title, 
          left: 'center', 
          top: 5,
          textStyle: { 
            fontSize: dynamicTitleSize, 
            fontWeight: 'bold', 
            color: '#000000' 
          }
        },
        
        grid: { top: 120, bottom: 130, left: '3%', right: '4%', containLabel: true },
        
        legend: [
          {
            data: [
              { name: L.pB, icon: 'roundRect', itemStyle: { color: '#cccccc' } },
              { name: L.eB, icon: 'roundRect', itemStyle: { color: '#63d1d3' } }
            ],
            bottom: 50, left: 'center', itemGap: 30, 
            textStyle: { fontSize: dynamicLegendSize, fontWeight: 'bold', color: '#000000' }
          },
          {
            data: [
              { name: L.pC, icon: 'circle', itemStyle: { color: '#ffcc00' } },
              { name: L.eC, icon: 'circle', itemStyle: { color: '#004b6b' } },
              { name: L.an, icon: 'roundRect', itemStyle: { color: '#1a0d33' } }
            ],
            bottom: 25, left: 'center', itemGap: 20, 
            textStyle: { fontSize: Math.max(10, dynamicLegendSize - 1), fontWeight: 'bold', color: '#000000' }
          }
        ],

        xAxis: { 
          type: 'category', 
          data: this.labels, 
          axisTick: { show: false },
          axisLabel: { 
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'bold',              
            color: '#243046',                 
            
            fontSize: dynamicAxisFontSize, 
            
            interval: 0, 
            margin: 15,
            width: dynamicBreakWidth,       
            overflow: 'break', 
            lineHeight: 15, 
            align: 'center'
          }       
        },

        yAxis: { 
          type: 'value', 
          min: 0, max: 100, interval: 20,
          splitLine: { show: true, lineStyle: { color: '#e0e0e0', width: 1.5, type: [5, 5] } }, 
          axisLabel: { formatter: "{value}%", fontWeight: 'bold', fontSize: 14 } 
        },

        series: [
          {
            type: 'custom', name: L.pB,
            renderItem: (params, api) => {

              const categoryWidth = api.size([0, 0])[0];
              const calculatedW = categoryWidth * 0.28;
              const w = Math.min(100, calculatedW); 
              
              const dynamicDepthX = Math.min(this.depthX, w * 0.3);

              const circleRadius = Math.min(18, (w / 2) - 1);
              
              const circleFontSize = Math.min(11, Math.max(8, circleRadius * 0.8));

              const idx = api.value(0);
              const pPerc = api.value(1);
              const ePerc = api.value(2);
              const center = api.coord([idx, 0]);
              const yBottom = center[1];
              const boxH = 25;
              const yP = api.coord([idx, pPerc])[1];
              const yE = api.coord([idx, ePerc])[1];

              const renderCubeWithIndicator = (xStart, yTop, color, val, textColor) => {
                const isZero = val <= 0;
                const startX = xStart + w / 2 + dynamicDepthX / 2;
                const startY = yTop - this.depthY / 2;
                const textX_diag = startX + (dynamicDepthX * 2);
                const textY_diag = startY - (this.depthY * 2) - 12;
                const lineEndX = startX + (dynamicDepthX * 1.6);
                const lineEndY = startY - (this.depthY * 1.6);
                const textX_zero = xStart + w / 2;
                const textY_zero = yBottom - 85;

                const elements = [];
                if (!isZero) {
                   elements.push(
                    { type: 'rect', shape: { x: xStart, y: yTop, width: w, height: yBottom - yTop }, style: { fill: color } },
                    { type: 'polygon', shape: { points: [[xStart, yTop], [xStart + dynamicDepthX, yTop - this.depthY], [xStart + w + dynamicDepthX, yTop - this.depthY], [xStart + w, yTop]] }, style: { fill: echarts.color.lift(color, 0.3) } },
                    { type: 'polygon', shape: { points: [[xStart + w, yTop], [xStart + w + dynamicDepthX, yTop - this.depthY], [xStart + w + dynamicDepthX, yBottom - this.depthY], [xStart + w, yBottom]] }, style: { fill: echarts.color.lift(color, -0.2) } },
                    { type: 'line', shape: { x1: startX, y1: startY, x2: lineEndX, y2: lineEndY }, style: { stroke: textColor, lineWidth: 2 } }
                  );
                }
                
                elements.push({
                  type: 'text', z: 30,
                  style: {
                    text: val + '%', fill: textColor, fontWeight: 'bold', 
                    fontSize: dynamicAxisFontSize, 
                    x: isZero ? textX_zero : textX_diag,
                    y: isZero ? textY_zero : textY_diag,
                    textAlign: 'center'
                  }
                });
                return elements;
              };

              return {
                type: 'group',
                children: [
                  ...renderCubeWithIndicator(center[0] - w, yP, '#cccccc', pPerc, '#333333'),
                  ...renderCubeWithIndicator(center[0], yE, '#63d1d3', ePerc, '#63d1d3'),

                  {
                    type: 'group', z: 10, children: [
                      { type: 'rect', shape: { x: center[0] - w, y: yBottom - boxH, width: w * 2, height: boxH, r: 2 }, style: { fill: '#1a0d33' } },
                      { type: 'text', style: { text: api.value(5).toString(), fill: '#fff', fontWeight: 'bold', fontSize: 11, x: center[0], y: yBottom - boxH / 2, textAlign: 'center', textVerticalAlign: 'middle' } }
                    ]
                  },

                  {
                    type: 'group', z: 20, children: [
                      { 
                        type: 'circle', 
                        shape: { 
                          cx: center[0] - w / 2, 
                          cy: yBottom - boxH - 22, 
                          r: circleRadius 
                        }, 
                        style: { fill: '#ffcc00' } 
                      },
                      { 
                        type: 'text', 
                        style: { 
                          text: api.value(3).toString(), 
                          fill: '#fff', 
                          fontWeight: 'bold', 
                          fontSize: circleFontSize, 
                          x: center[0] - w / 2, 
                          y: yBottom - boxH - 22, 
                          textAlign: 'center', 
                          textVerticalAlign: 'middle' 
                        } 
                      },
                      
                      { 
                        type: 'circle', 
                        shape: { 
                          cx: center[0] + w / 2, 
                          cy: yBottom - boxH - 22, 
                          r: circleRadius 
                        }, 
                        style: { fill: '#004b6b' } 
                      },
                      { 
                        type: 'text', 
                        style: { 
                          text: api.value(4).toString(), 
                          fill: '#fff', 
                          fontWeight: 'bold', 
                          fontSize: circleFontSize, 
                          x: center[0] + w / 2, 
                          y: yBottom - boxH - 22, 
                          textAlign: 'center', 
                          textVerticalAlign: 'middle' 
                        } 
                      }
                    ]
                  }
                ]
              };
            },
            data: this.labels.map((l, i) => [i, this.datasets[0].data[i], this.datasets[1].data[i], this.datasets[0].rawAmounts[i], this.datasets[1].rawAmounts[i], this.annualTotals ? this.annualTotals[i] : 0])
          },
          { name: L.eB, type: 'bar', data: [] }, { name: L.pC, type: 'scatter', data: [] }, { name: L.eC, type: 'scatter', data: [] }, { name: L.an, type: 'bar', data: [] }
        ]
      };
      
      this.chartInstance.setOption(option, true);
    }
  }
};
</script>